import { useState, useCallback, useRef } from 'react';
import { getTemplate } from '../services/api/apis';
import type { CertificateFormData } from '../types';
import type { PrescriptionFormData } from './formHooks/usePrescriptionForm';
import { generateAndDownloadPdf, generateAndDownloadMergedPdf } from '../services/pdf/pdfGenerator';
import { buildCertificateInputs, buildPrescriptionInputs } from '../services/pdf/pdfInputBuilder';
import { uploadCertificatePdf } from '../services/pdf/certificateUpload';

const CERT_TEMP_ID = 'GENERAL_CERTIFICATE';
const RX_TEMP_ID = 'PRESCRIPTION';

interface PendingUpload {
  blob: Blob;
  docId: string;
  certificateType: string;
}

export interface UsePdfGenerationOptions {
  onUploaded?: (certificateId: string) => void;
}

export interface UsePdfGenerationReturn {
  certTemplateReady: boolean;
  rxTemplateReady: boolean;
  isLoadingTemplates: boolean;
  templateError: string | null;
  isGenerating: boolean;
  generateError: string | null;
  uploadState: 'idle' | 'loading' | 'success' | 'error';
  uploadError: string | null;
  pendingUpload: boolean;
  fetchTemplates: () => Promise<void>;
  generateCertificatePdf: (formData: CertificateFormData, patientDocId: string) => Promise<void>;
  generateMergedPdf: (formData: CertificateFormData, rxData: PrescriptionFormData, patientDocId: string) => Promise<void>;
  confirmUpload: () => void;
  discardUpload: () => void;
}

type TemplateCache = { fieldSchema: string[]; templateSchema: any } | null;

export function usePdfGeneration(options?: UsePdfGenerationOptions): UsePdfGenerationReturn {
  const [certTemplate, setCertTemplate] = useState<TemplateCache>(null);
  const [rxTemplate, setRxTemplate] = useState<TemplateCache>(null);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingUpload | null>(null);

  const onUploadedRef = useRef<UsePdfGenerationOptions['onUploaded']>(undefined);
  onUploadedRef.current = options?.onUploaded;

  const fetchTemplates = useCallback(async () => {
    if (certTemplate && rxTemplate) return;
    setIsLoadingTemplates(true);
    setTemplateError(null);
    try {
      const [certRes, rxRes] = await Promise.all([
        getTemplate(CERT_TEMP_ID),
        getTemplate(RX_TEMP_ID),
      ]);
      if (certRes.success && certRes.data) {
        setCertTemplate({ fieldSchema: certRes.data.fieldSchema, templateSchema: certRes.data.templateSchema });
      }
      if (rxRes.success && rxRes.data) {
        setRxTemplate({ fieldSchema: rxRes.data.fieldSchema, templateSchema: rxRes.data.templateSchema });
      }
      if (!certRes.success && !rxRes.success) {
        setTemplateError('Failed to load templates');
      }
    } catch (err) {
      setTemplateError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [certTemplate, rxTemplate]);

  const generateCertificatePdf = useCallback(async (
    formData: CertificateFormData,
    patientDocId: string
  ) => {
    if (!certTemplate) return;
    setIsGenerating(true);
    setGenerateError(null);
    setUploadState('idle');
    setUploadError(null);
    setPending(null);
    try {
      const inputs = buildCertificateInputs(formData, certTemplate.fieldSchema);
      const blob = await generateAndDownloadPdf(certTemplate.templateSchema, inputs, `${formData.patientName}_${patientDocId}.pdf`);
      setPending({ blob, docId: patientDocId, certificateType: formData.certificateType });
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : 'PDF generation failed');
    } finally {
      setIsGenerating(false);
    }
  }, [certTemplate]);

  const generateMergedPdf = useCallback(async (
    formData: CertificateFormData,
    rxData: PrescriptionFormData,
    patientDocId: string
  ) => {
    if (!certTemplate || !rxTemplate) return;
    setIsGenerating(true);
    setGenerateError(null);
    setUploadState('idle');
    setUploadError(null);
    setPending(null);
    try {
      const certInputs = buildCertificateInputs(formData, certTemplate.fieldSchema);
      const rxInputs = buildPrescriptionInputs(rxData, rxTemplate.fieldSchema);
      const blob = await generateAndDownloadMergedPdf(
        certTemplate.templateSchema, certInputs,
        rxTemplate.templateSchema, rxInputs,
        `${formData.patientName}_${patientDocId}.pdf`
      );
      setPending({ blob, docId: patientDocId, certificateType: formData.certificateType });
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : 'PDF generation failed');
    } finally {
      setIsGenerating(false);
    }
  }, [certTemplate, rxTemplate]);

  const confirmUpload = useCallback(() => {
    if (!pending) return;
    const { blob, docId, certificateType } = pending;
    setUploadState('loading');
    setUploadError(null);
    uploadCertificatePdf(docId, certificateType, blob)
      .then(({ certificate_id }) => {
        setUploadState('success');
        setPending(null);
        onUploadedRef.current?.(certificate_id);
      })
      .catch((err) => {
        setUploadState('error');
        setUploadError(err instanceof Error ? err.message : 'Upload failed');
      });
  }, [pending]);

  const discardUpload = useCallback(() => {
    setPending(null);
    setUploadState('idle');
    setUploadError(null);
  }, []);

  return {
    certTemplateReady: !!certTemplate,
    rxTemplateReady: !!rxTemplate,
    isLoadingTemplates,
    templateError,
    isGenerating,
    generateError,
    uploadState,
    uploadError,
    pendingUpload: !!pending,
    fetchTemplates,
    generateCertificatePdf,
    generateMergedPdf,
    confirmUpload,
    discardUpload,
  };
}
